import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSubcontractors, createSubcontractor, getCoiWording } from '../api';
import { Icon, SegmentedControl, StateScreen } from '../components/ui';
import { usePageSearch, matchesSearch, noMatchText } from '../lib/pageSearch';
import { docCell, jobDocNeeds, standingOf, todayYmd, fmtDay, lastJobText } from '../lib/subcontractors';
import { StatusText, StandingPill, PrimaryButton, SecondaryButton, SubStyles } from '../components/subs/SubUi';
import { CoiWordingDialog } from '../components/subs/JobDocuments';
import SubcontractorForm from '../components/subs/SubcontractorForm';
import SubDashboard from '../components/subs/SubDashboard';

/* ═══════════════════════════════════════════════════════════════════════════
   Subcontractor Mgmt (Duncan 2026-10-20): "a place where we manage the admin side of the
   subcontractors standing with us. Master service agreements, other documents and info and history
   with us." Summit holds it; the subcontractor portal only shows subs their jobs.
   Every sub: its trades, standing, MSA, W-9, its documents on its open jobs and how many jobs it's on. A row
   opens its page (info, documents, job history, notes) -- nothing is edited from the list.
   Duncan 2026-10-22: a certificate of insurance and a safety plan are each job's, so the list shows how many of
   a sub's open jobs still need theirs; the COI wording subs' insurers use lives here (COI wording).
   Duncan 2026-10-21: the main screen is a dashboard (components/subs/SubDashboard.js); the list is the
   "All subcontractors" view beside it, and searching opens it.
   ═══════════════════════════════════════════════════════════════════════════ */

// The list in AR's table look (Duncan 2026-10-21: "matches our really nice pages like job cost").
const COLS = 'minmax(0, 2fr) minmax(0, 0.9fr) minmax(0, 1fr) 54px minmax(0, 1.3fr) 54px minmax(0, 0.9fr)';
const shell = { background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' };
const th = { fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' };
// A flag's cell goes to the spot where its paper is filed (Duncan 2026-10-22): the sub's row on its Documents tab.
const FlagCell = ({ onGo, label, children, center }) => (
  <button type="button" onClick={(e) => { e.stopPropagation(); onGo(); }} title={`${label}: show where it’s filed`}
    style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer', font: 'inherit', textAlign: center ? 'center' : 'left', minWidth: 0 }}>
    {children}
  </button>
);
const Check = ({ on }) => (on
  ? <span style={{ color: 'var(--success)', fontWeight: 700 }} aria-label="on file">✓</span>
  : <span style={{ color: 'var(--danger)', fontWeight: 700 }} aria-label="missing">✕</span>);

/** A sub's documents across its open jobs: how many still need their COI or safety plan. { text, tone }. */
const jobDocsCell = (pairs, today) => {
  if (!pairs || !pairs.length) return { text: '—', tone: 'var(--text-4)' };
  const owing = pairs.map((j) => jobDocNeeds(j.documents, today)).filter((n) => n.length);
  if (!owing.length) return { text: `in order (${pairs.length} job${pairs.length === 1 ? '' : 's'})`, tone: null };
  const red = owing.some((n) => n.some((x) => x.tone === 'var(--danger)'));
  return { text: `${owing.length} of ${pairs.length} job${pairs.length === 1 ? '' : 's'} missing`, tone: red ? 'var(--danger)' : 'var(--warning)' };
};

const SubcontractorsPage = () => {
  const navigate = useNavigate();
  const search = usePageSearch('Search subcontractors…');
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'all' ? 'all' : 'dashboard';
  const setView = useCallback((v) => setParams(v === 'all' ? { view: 'all' } : {}, { replace: true }), [setParams]);
  const [subs, setSubs] = useState(null);
  const [usage, setUsage] = useState([]);
  const [jobDocs, setJobDocs] = useState([]);
  const [wording, setWording] = useState(null); // the COI wording dialog: null, or its text
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  // Typing a search means looking for a sub: the list shows it.
  useEffect(() => { if (search.trim() && view !== 'all') setView('all'); }, [search, view, setView]);

  const load = useCallback(async () => {
    try {
      const data = await getSubcontractors();
      setSubs(data.subcontractors || []);
      setUsage(data.usage_by_year || []);
      setJobDocs(data.job_documents || []);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load subcontractors');
      setSubs((s) => s || []);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => (subs || []).filter((s) => matchesSearch(search,
    s.name, s.trades, standingOf(s.standing).label, s.contact_name, s.contact_email, s.city, s.state)), [subs, search]);

  const add = async (payload) => {
    const created = await createSubcontractor(payload);
    setAdding(false);
    navigate(`/subcontractors/${created.id}`);
  };
  // A flag opens its row: the sub's Documents tab (?focus=msa | w9 | jobs), or a job's sub card (?sub=&focus=coi | safety).
  const open = (s, focus) => navigate(`/subcontractors/${s.id}${focus ? `?tab=documents&focus=${focus}` : ''}`);
  const openJob = (j, focus) => navigate(`/projects/${j.slug || j.project_id}/subcontractors${j.subcontractor_id ? `?sub=${j.subcontractor_id}&focus=${focus || 'documents'}` : ''}`);
  const openWording = async () => {
    try {
      setWording((await getCoiWording()).wording || '');
    } catch (e) {
      setError(e.message || 'Failed to read the COI wording');
    }
  };
  const today = todayYmd();
  const pairsBySub = useMemo(() => {
    const m = new Map();
    jobDocs.forEach((j) => m.set(j.subcontractor_id, [...(m.get(j.subcontractor_id) || []), j]));
    return m;
  }, [jobDocs]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SubStyles />
      {error && <div className="alert alert-error">{error}</div>}
      <div style={{ ...shell, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '10px 12px' }}>
        <SegmentedControl value={view} onChange={setView} options={[
          { value: 'dashboard', label: 'Dashboard' },
          { value: 'all', label: 'All subcontractors', count: subs ? subs.length : undefined },
        ]} />
        <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Subcontractor records in Summit · subcontracts from CE · as of {fmtDay(today)}</span>
        <span style={{ flex: 1 }} />
        <SecondaryButton onClick={openWording} title="The paragraph subs' insurance agents put on a job's certificate of insurance">COI wording</SecondaryButton>
        <PrimaryButton onClick={() => setAdding(true)}><Icon name="plus" size={14} stroke={2.2} />New subcontractor</PrimaryButton>
      </div>
      {view === 'dashboard' && (subs
        ? <SubDashboard subs={subs} usage={usage} jobDocs={jobDocs} onOpen={open} onOpenJob={openJob} />
        : <div style={{ fontSize: 13.5, color: 'var(--text-3)' }}>Loading subcontractors…</div>)}
      {view === 'all' && (
      <section style={{ ...shell, overflow: 'hidden' }} aria-label="Subcontractors">
        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 14, alignItems: 'center', padding: '11px 18px', background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-default)' }}>
          <span style={th}>Company</span><span style={th}>Standing</span><span style={th}>MSA</span>
          <span style={{ ...th, textAlign: 'center' }}>W-9</span><span style={th} title="Each open job's certificate of insurance and job site safety plan">Job documents</span>
          <span style={{ ...th, textAlign: 'right' }}>Jobs</span><span style={{ ...th, textAlign: 'right' }}>Last job</span>
        </div>
        {!subs ? (
          <div style={{ padding: 16, fontSize: 13.5, color: 'var(--text-3)' }}>Loading subcontractors…</div>
        ) : shown.length === 0 ? (
          <div style={{ padding: '4px 12px 14px' }}>
            {search.trim() ? <StateScreen kind="empty" {...noMatchText(search)} />
              : <StateScreen kind="empty" title="No subcontractors yet" body="Add a subcontractor to keep their MSA, W-9, each job's documents, job history and notes in one place." />}
          </div>
        ) : shown.map((s) => (
          <div key={s.id} className="sub-row" role="button" tabIndex={0} aria-label={`Open ${s.name}`}
            onClick={() => open(s)} onKeyDown={(e) => { if (e.key === 'Enter') open(s); }}
            style={{ display: 'grid', gridTemplateColumns: COLS, gap: 14, alignItems: 'center', padding: '12px 18px', borderTop: '1px solid var(--border-subtle)', cursor: 'pointer', fontSize: 14 }}>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={{ fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
              <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.trades.length ? s.trades.join(', ') : 'No trade set'}</span>
            </span>
            <span><StandingPill standing={s.standing} /></span>
            <FlagCell label="MSA" onGo={() => open(s, 'msa')}><StatusText status={docCell(s.msa, today)} /></FlagCell>
            <FlagCell label="W-9" center onGo={() => open(s, 'w9')}><Check on={s.w9} /></FlagCell>
            <FlagCell label="Job documents" onGo={() => open(s, 'jobs')}><StatusText status={jobDocsCell(pairsBySub.get(s.id), today)} /></FlagCell>
            <span className="mono" style={{ textAlign: 'right', fontWeight: 700, color: s.jobs ? 'var(--text-1)' : 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{s.jobs}</span>
            <span className="mono" style={{ textAlign: 'right', fontSize: 13, color: s.on_job_now ? 'var(--success)' : s.last_job ? 'var(--text-2)' : s.next_job ? 'var(--text-3)' : 'var(--text-4)', fontWeight: s.on_job_now ? 600 : undefined, whiteSpace: 'nowrap' }}>{lastJobText(s)}</span>
          </div>
        ))}
      </section>
      )}
      {adding && <SubcontractorForm sub={null} onClose={() => setAdding(false)} onSubmit={add} />}
      {wording !== null && <CoiWordingDialog initial={wording} onClose={() => setWording(null)} onSaved={() => setWording(null)} />}
    </div>
  );
};

export default SubcontractorsPage;
